import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { toggleAttended, fetchBatchEventStatus } from '../attendedUtils';

function FestivalDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [search, setSearch] = useState('');

    // Attended state
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (eventList) => {
        if (!eventList || eventList.length === 0) return;
        try {
            const eventIds = eventList.map(e => e.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const attendedMap2 = {};
            const performedMap2 = {};
            const workedMap2 = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                attendedMap2[id] = status.attended;
                performedMap2[id] = status.performed || false;
                workedMap2[id] = status.worked || false;
            });
            setAttendedMap(attendedMap2);
            setPerformedMap(performedMap2);
            setWorkedMap(workedMap2);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    useEffect(() => {
        axios.get(`${config.API_URL}/festivals/${slug}`)
            .then(res => {
                setData(res.data);
                setLoading(false);
                if (res.data && res.data.festival) {
                    document.title = `SHARC - ${res.data.festival.name}`;
                }
                if (res.data && res.data.events) {
                    loadAttendedStatus(res.data.events);
                }
            })
            .catch(() => {
                setError('Failed to load festival');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    if (loading) return <div style={styles.message}>Loading festival...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { festival, events, promoters } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const formatDateLong = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const start = formatDate(festival.start_date);
    const end = formatDate(festival.end_date);
    const dateRange = start && end && start !== end
        ? `${start} – ${end}`
        : start || null;

    const q = search.toLowerCase();
    const filtered = search
        ? events.filter(e => [
            e.event_date ? formatDateLong(e.event_date) : '',
            e.venue_name, e.city, e.state, e.country, e.artists
          ].some(v => v && v.toLowerCase().includes(q)))
        : events;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/festivals" style={styles.backLink}>← Back to Festivals</Link>
                <Link to={`/festivals/${festival.slug || festival.id}/edit`} style={styles.editButton}>Edit Festival</Link>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <div style={festival.image_url ? styles.headerWithImage : undefined}>
                    {festival.image_url && (
                        <img
                            src={festival.image_url}
                            alt={festival.name}
                            style={styles.festivalImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{festival.name}</h1>
                        {dateRange && <div style={styles.dateRange}>{dateRange}</div>}
                        {promoters && promoters.length > 0 && (
                            <div style={styles.promoterRow}>
                                {promoters.map((p, i) => (
                                    <span key={p.id}>
                                        <Link to={`/promoters/${p.slug}`} style={styles.promoterLink}>{p.name}</Link>
                                        {i < promoters.length - 1 && <span style={{ color: '#555', margin: '0 4px' }}>·</span>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {festival.description && (
                    <div style={styles.bio}>{festival.description}</div>
                )}
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{events.length}</div>
                    <div style={styles.statLabel}>Events</div>
                </div>
                {festival.start_date && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{formatDate(festival.start_date)}</div>
                        <div style={styles.statLabel}>Start Date</div>
                    </div>
                )}
                {festival.end_date && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{formatDate(festival.end_date)}</div>
                        <div style={styles.statLabel}>End Date</div>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    <button
                        onClick={() => { setActiveTab(0); setSearch(''); }}
                        style={{ ...styles.tabBtn, ...(activeTab === 0 ? styles.tabBtnActive : {}) }}
                    >
                        Events
                    </button>
                </div>

                <div style={styles.tabContent}>
                    {events.length === 0 ? (
                        <div style={styles.emptyTab}>No events linked to this festival yet.</div>
                    ) : (
                        <>
                            <input
                                type="text"
                                placeholder="Search by date, venue, city, artists..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={styles.tabSearch}
                            />
                            {filtered.length === 0 ? (
                                <div style={styles.emptyTab}>No events match your search.</div>
                            ) : (
                                <div style={styles.tableContainer}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Date</th>
                                                <th style={styles.th}>City</th>
                                                <th style={styles.th}>State</th>
                                                <th style={styles.th}>Country</th>
                                                <th style={styles.th}>Venue</th>
                                                <th style={styles.th}>Artists</th>
                                                <th style={styles.th}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map((e, idx) => {
                                                const dateObj = e.event_date ? new Date(e.event_date) : null;
                                                const formattedDate = dateObj
                                                    ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                                    : '—';
                                                const isAttended = attendedMap[e.event_id] === true;
                                                const isPerformed = performedMap[e.event_id] === true;
                                                const isWorked = workedMap[e.event_id] === true;
                                                const isToggling = togglingMap[e.event_id];
                                                return (
                                                    <tr key={e.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                                        <td style={styles.td}>
                                                            <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                {formattedDate}
                                                            </Link>
                                                        </td>
                                                        <td style={styles.td}>{e.city || '—'}</td>
                                                        <td style={styles.td}>{e.state || '—'}</td>
                                                        <td style={styles.td}>{e.country || '—'}</td>
                                                        <td style={styles.td}>
                                                            <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                {e.venue_name}
                                                            </Link>
                                                        </td>
                                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                            {e.artists || '—'}
                                                        </td>
                                                        <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            {isPerformed ? (
                                                                <span style={styles.performedBadge}>Performed</span>
                                                            ) : isWorked ? (
                                                                <span style={styles.workedBadge}>Worked</span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleToggleAttended(e.event_id)}
                                                                    disabled={isToggling}
                                                                    style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                                >
                                                                    {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    festivalImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    dateRange: { fontSize: '18px', color: '#bb86fc', marginBottom: '8px', fontWeight: '500' },
    promoterRow: { fontSize: '14px', color: '#aaa', marginTop: '4px' },
    promoterLink: { color: '#bb86fc', textDecoration: 'none' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #2a2a2a' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default FestivalDetail;
